import { BlockComponent } from "../../../framework/src/BlockComponent";
import { runEngine } from "../../../framework/src/RunEngine";
import { Message } from "../../../framework/src/Message";
import { IBlock } from "../../../framework/src/IBlock";
import MessageEnum, { getName } from "../../../framework/src/Messages/MessageEnum";
import { debounce } from "lodash";
import { makeApiMessage as MakeApiMessage } from '../../../components/src/common';

// Customizable Area Start
export const configJSON = require("./config");

export interface IOption {
  id: number | string;
  name: string;
  label?: string;
  option?: string;
  store_name?: string;
}

export interface IPromotionItem {
  id?: number;
  item_type: string;
  catalogue_id: number | null;
  _destroy?: boolean;
}

export interface IPromotionOfferItem {
  id?: number;
  offer_type: string;
  item_type?: string;
  catalogue_id?: number | null;
  _destroy?: boolean;
}

export interface IExcludedDay {
  id?: number;
  day_of_week: string;
  _destroy?: boolean;
}

export interface IExcludedDate {
  id?: number;
  excluded_date: string | Date | null;
  reason: string;
  _destroy?: boolean;
}

export interface IExcludedTime {
  id?: number;
  start_time: string;
  end_time: string;
  _destroy?: boolean;
}

export interface IPromotionData {
  id?: string;
  attributes: {
    promotion_name: string;
    promotion_type_id: number | null;
    promo_item_text_en: string;
    promo_item_text_ar: string;
    order_discount_name_en: string;
    order_discount_name_ar: string;
    used: string;
    no_of_orders: number | null;
    minimum_order_amount: string;
    minimum_items: number;
    start_date: Date | null;
    end_date: Date | null;
    status: string;
    all_sections_enabled: boolean;
    all_products_enabled: boolean;
    section_ids: number[];
    product_ids: number[];
    service_ids: number[];
    platform_ids: number[];
    region_ids: number[];
    area_ids: number[];
    store_ids: number[];
    promotion_items: IPromotionItem[];
    promotion_offer_items: IPromotionOfferItem[];
    excluded_days: IExcludedDay[];
    excluded_dates: IExcludedDate[];
    excluded_times: IExcludedTime[];
  };
}

const usageTypes = [
  { id: "one_order", name: "One Order Only", label: "One Order Only" },
  { id: "all_orders", name: "All Orders", label: "All Orders" },
  { id: "specific_number_of_orders", name: "Specific Number of Orders", label: "Specific Number of Orders" }
];

const statusTypes = [
  { id: "active", name: "Active", label: "Active" },
  { id: "inactive", name: "Inactive", label: "Inactive" }
];

const itemTypes = [
  { id: "single_product", name: "Single Product", label: "Single Product" },
  { id: "parent_product", name: "Product Group", label: "Product Group" },
  { id: "all_products", name: "All Products", label: "All Products" }
];

// "All Products" only makes sense for the whole promotion, not for a specific offer item
const offerItemTypes = itemTypes.filter(type => type.id !== "all_products");

const offerTypes = [
  { id: "same_item", name: "Same Item", label: "Same Item" },
  { id: "different_item", name: "Different Specific Item", label: "Different Specific Item" }
];

const daysOfWeek = [
  { id: "sunday", name: "Sunday", option: "Sunday" },
  { id: "monday", name: "Monday", option: "Monday" },
  { id: "tuesday", name: "Tuesday", option: "Tuesday" },
  { id: "wednesday", name: "Wednesday", option: "Wednesday" },
  { id: "thursday", name: "Thursday", option: "Thursday" },
  { id: "friday", name: "Friday", option: "Friday" },
  { id: "saturday", name: "Saturday", option: "Saturday" }
];

// Customizable Area End

export interface Props {
  navigation: {
    navigate: Function;
    getParam: Function;
  };
  id: string;
}

export interface S {
  // Customizable Area Start
  data: IPromotionData;
  error: any;
  isLoading: boolean;
  isSaveLoader: boolean;
  noData: boolean;
  saveStatus: string;
  
  // Dropdown Lists
  promotionTypes: IOption[];
  regionList: IOption[];
  areaList: IOption[];
  sectionList: IOption[];
  serviceList: IOption[];
  productList: IOption[];
  platformList: IOption[];
  storeList: IOption[];
  
  // Selected Values for Multi-Select (Legacy - keeping for backward compatibility)
  selectedRegions: IOption[];
  selectedAreas: IOption[];
  selectedSections: IOption[];
  selectedProducts: IOption[];
  selectedPlatforms: IOption[];
  selectedStores: IOption[];
  
  // Region Multi-Select State (following promocodes pattern)
  regionSelectedData: { id: string, option: string }[];
  selectedRegionsForList: { id: string, option: string }[];
  isRegionSelectAll: boolean;
  
  // Area Multi-Select State (following promocodes pattern)
  areaSelectedData: { id: string, option: string }[];
  selectedAreasForList: { id: string, option: string }[];
  isAreaSelectAll: boolean;
  
  // Section Multi-Select State (following promocodes pattern)
  sectionSelectedData: { id: string, option: string }[];
  selectedSectionsForList: { id: string, option: string }[];
  isSectionSelectAll: boolean;
  sectionLists: { id: string, option: string }[];
  
  // Services Multi-Select State (following promocodes pattern)
  selectedServices: { id: string, option: string }[];
  selectedServicesForList: { id: string, option: string }[];
  isServiceSelectAll: boolean;
  serviceOptions: { id: string, option: string }[];
  
  // Platform Multi-Select State (following promocodes pattern)
  selectedPlatform: { id: string, option: string }[];
  platformSelectedOptionForList: { id: string, option: string }[];
  isPlatformSelectAll: boolean;
  
  // Store Multi-Select State (following promocodes pattern)
  selectedStoresForList: { id: string, option: string }[];
  isStoreSelectAll: boolean;
  
  // Product Multi-Select State (following promocodes pattern)
  selectedForAdd: { id: string, option: string }[];
  addSpecSelectedOptionForList: { id: string, option: string }[];
  isSelectedAll: boolean;
  productListItemsDropdown: { id: string, option: string }[];
  
  // Promotion Items Product State
  promotionItemProductListDropdown: { id: string, option: string }[];
  
  // Offer Items Product State
  offerItemProductListDropdown: { id: string, option: string }[];
  
  // Excluded Days Multi-Select State (following promocodes pattern)
  selectedExcludeDays: { id: string, option: string }[];
  excludeDaysSelectedOptionForList: { id: string, option: string }[];
  isExcludeDaysSelectAll: boolean;
  excludeDayList: { id: string, option: string }[];
  
  // UI State
  showEndDate: boolean;
  
  // Customizable Area End
}

export interface SS {}

export default class PromotionCreateController extends BlockComponent<Props, S, SS> {
  // Customizable Area Start
  createPromotionApiCallId: string = "";
  updatePromotionApiCallId: string = "";
  getPromotionApiCallId: string = "";
  getPromotionTypesApiCallId: string = "";
  getRegionsApiCallId: string = "";
  getAreasApiCallId: string = "";
  getSectionsApiCallId: string = "";
  getServicesApiCallId: string = "";
  getProductsApiCallId: string = "";
  getPromotionItemProductsApiCallId: string = "";
  getOfferItemProductsApiCallId: string = "";
  getPlatformsApiCallId: string = "";
  getStoresApiCallId: string = "";
  getExcludeDaysApiCallId: string = "";
  
  isEdit: boolean = false;
  promotionId: string = "";
  debouncedFunction: Function;
  
  // Product pagination for main products (legacy)
  productNextPage: number = 1;
  isLoadingMoreProducts: boolean = false;
  hasMoreProducts: boolean = true;
  productSearchQuery: string = "";
  
  // Promotion Items product pagination
  promotionItemProductNextPage: number = 1;
  isLoadingMorePromotionItemProducts: boolean = false;
  hasMorePromotionItemProducts: boolean = true;
  promotionItemProductSearchQuery: string = "";
  
  // Offer Items product pagination
  offerItemProductNextPage: number = 1;
  isLoadingMoreOfferItemProducts: boolean = false;
  hasMoreOfferItemProducts: boolean = true;
  offerItemProductSearchQuery: string = "";
  
  // Temporary storage for edit mode data restoration
  tempAreaDataForEdit: any[] = [];
  tempStoreDataForEdit: any[] = [];
  // Customizable Area End

  constructor(props: Props) {
    super(props);
    // Customizable Area Start
    this.subScribedMessages = [
      getName(MessageEnum.RestAPIResponceMessage),
      getName(MessageEnum.RestAPIResponceSuccessMessage),
      getName(MessageEnum.RestAPIResponceDataMessage),
      getName(MessageEnum.SessionResponseMessage),
    ];

    this.receive = this.receive.bind(this);
    runEngine.attachBuildingBlock(this as IBlock, this.subScribedMessages);

    this.state = {
      data: {
        attributes: {
          promotion_name: "",
          promotion_type_id: null,
          promo_item_text_en: "",
          promo_item_text_ar: "",
          order_discount_name_en: "",
          order_discount_name_ar: "",
          used: "all_orders",
          no_of_orders: null,
          minimum_order_amount: "",
          minimum_items: 1,
          start_date: null,
          end_date: null,
          status: "active",
          all_sections_enabled: false,
          all_products_enabled: false,
          section_ids: [],
          product_ids: [],
          service_ids: [],
          platform_ids: [],
          region_ids: [],
          area_ids: [],
          store_ids: [],
          promotion_items: [{ item_type: "single_product", catalogue_id: null }],
          promotion_offer_items: [{ offer_type: "same_item" }],
          excluded_days: [],
          excluded_dates: [],
          excluded_times: []
        }
      },
      error: {},
      isLoading: false,
      isSaveLoader: false,
      noData: false,
      saveStatus: "",
      promotionTypes: [],
      regionList: [],
      areaList: [],
      sectionList: [],
      serviceList: [],
      productList: [],
      platformList: [],
      storeList: [],
      selectedRegions: [],
      selectedAreas: [],
      selectedSections: [],
      selectedProducts: [],
      selectedPlatforms: [],
      selectedStores: [],
      showEndDate: false,
      
      // Region Multi-Select State (following promocodes pattern)
      regionSelectedData: [],
      selectedRegionsForList: [],
      isRegionSelectAll: false,
      
      // Area Multi-Select State (following promocodes pattern)
      areaSelectedData: [],
      selectedAreasForList: [],
      isAreaSelectAll: false,
      
      // Section Multi-Select State (following promocodes pattern)
      sectionSelectedData: [],
      selectedSectionsForList: [],
      isSectionSelectAll: false,
      sectionLists: [],
      
      // Services Multi-Select State (following promocodes pattern)
      selectedServices: [],
      selectedServicesForList: [],
      isServiceSelectAll: false,
      serviceOptions: [],
      
      // Platform Multi-Select State (following promocodes pattern)
      selectedPlatform: [],
      platformSelectedOptionForList: [],
      isPlatformSelectAll: false,
      
      // Store Multi-Select State (following promocodes pattern)
      selectedStoresForList: [],
      isStoreSelectAll: false,
      
      // Product Multi-Select State (following promocodes pattern)
      selectedForAdd: [],
      addSpecSelectedOptionForList: [],
      isSelectedAll: false,
      productListItemsDropdown: [],
      
      // Promotion Items Product State
      promotionItemProductListDropdown: [],
      
      // Offer Items Product State
      offerItemProductListDropdown: [],
      
      // Excluded Days Multi-Select State (following promocodes pattern)
      selectedExcludeDays: [],
      excludeDaysSelectedOptionForList: [],
      isExcludeDaysSelectAll: false,
      excludeDayList: [] // Will be populated from API
    };

    this.debouncedFunction = debounce((value: string, callback: Function) => {
      callback(value);
    }, 500);
    // Customizable Area End
  }

  async receive(from: string, message: Message) {
    // Customizable Area Start
    const requestId = message.getData(
      getName(MessageEnum.RestAPIResponceDataMessage)
    );

    const successResponse = message.getData(
      getName(MessageEnum.RestAPIResponceSuccessMessage)
    );

    if (successResponse) {
      if (requestId === this.createPromotionApiCallId) {
        this.handleCreatePromotionResponse(successResponse);
      } else if (requestId === this.updatePromotionApiCallId) {
        this.handleUpdatePromotionResponse(successResponse);
      } else if (requestId === this.getPromotionApiCallId) {
        this.handleGetPromotionResponse(successResponse);
      } else if (requestId === this.getPromotionTypesApiCallId) {
        this.handlePromotionTypesResponse(successResponse);
      } else if (requestId === this.getRegionsApiCallId) {
        this.handleRegionsResponse(successResponse);
      } else if (requestId === this.getAreasApiCallId) {
        this.handleAreasResponse(successResponse);
      } else if (requestId === this.getSectionsApiCallId) {
        this.handleSectionsResponse(successResponse);
      } else if (requestId === this.getServicesApiCallId) {
        this.handleServicesResponse(successResponse);
      } else if (requestId === this.getProductsApiCallId) {
        this.handleProductsResponse(successResponse);
      } else if (requestId === this.getPromotionItemProductsApiCallId) {
        this.handlePromotionItemProductsResponse(successResponse);
      } else if (requestId === this.getOfferItemProductsApiCallId) {
        this.handleOfferItemProductsResponse(successResponse);
      } else if (requestId === this.getPlatformsApiCallId) {
        this.handlePlatformsResponse(successResponse);
      } else if (requestId === this.getStoresApiCallId) {
        this.handleStoresResponse(successResponse);
      } else if (requestId === this.getExcludeDaysApiCallId) {
        this.handleExcludeDaysResponse(successResponse);
      }
    }
    // Customizable Area End
  }

  // Customizable Area Start
  async componentDidMount() {
    const promotionId = this.props.navigation.getParam("id");
    if (promotionId) {
      this.isEdit = true;
      this.promotionId = promotionId;
      this.getPromotionDetails();
    }
    
    // Load all dropdown data
    this.getPromotionTypes();
    this.getRegions();
    this.getSections();
    this.getServices();
    // Don't load products initially - they will be loaded when sections are selected
    this.getPlatforms();
    this.getExcludeDays();
  }

  getPromotionDetails = () => {
    const requestMessage = MakeApiMessage({
      url: `${configJSON.ApiUrls.promotionShow}${this.promotionId}`,
      method: "GET",
    });

    this.getPromotionApiCallId = requestMessage.messageId;
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  getPromotionTypes = () => {
    const requestMessage = MakeApiMessage({
      url: "/bx_block_promotions/promotions/promotion_types",
      method: "GET",
    });

    this.getPromotionTypesApiCallId = requestMessage.messageId;
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  getRegions = () => {
    const requestMessage = MakeApiMessage({
      url: "/bx_block_regions/regions?dropdown=true",
      method: "GET",
    });

    this.getRegionsApiCallId = requestMessage.messageId;
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  getAreas = (regionIds: number[]) => {
    if (regionIds.length === 0) {
      this.setState({ areaList: [], selectedAreas: [] });
      return;
    }

    const regionQuery = regionIds.map(id => `region_id[]=${id}`).join("&");
    const requestMessage = MakeApiMessage({
      url: `/bx_block_regions/areas?${regionQuery}&dropdown=true`,
      method: "GET",
    });

    this.getAreasApiCallId = requestMessage.messageId;
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  getSections = () => {
    const requestMessage = MakeApiMessage({
      url: "/bx_block_categories/categories?dropdown=true",
      method: "GET",
    });

    this.getSectionsApiCallId = requestMessage.messageId;
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  getServices = () => {
    const requestMessage = MakeApiMessage({
      url: "/bx_block_categories/services?dropdown=true",
      method: "GET",
    });

    this.getServicesApiCallId = requestMessage.messageId;
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  getProducts = (sectionIds: number[] = [], pageNo: number = 1, searchQuery: string = "") => {
    // Don't load products if no sections selected
    if (sectionIds.length === 0 && !this.state.isSectionSelectAll) {
      this.setState({ 
        productList: [], 
        productListItemsDropdown: [],
        selectedProducts: [] 
      });
      return;
    }

    // Build filter query
    let url = "/bx_block_catalogue/catalogues?dropdown=true";
    
    // Add section filter if sections are selected
    if (sectionIds.length > 0 && !this.state.isSectionSelectAll) {
      const sectionFilter = sectionIds.join(",");
      url += `&filter_by[section_ids]=${sectionFilter}`;
    }
    
    // Add search query if provided
    if (searchQuery) {
      url += `&filter_by[query]=${searchQuery}`;
    }
    
    // Add pagination
    url += `&page_no=${pageNo}&per_page=20`;

    const requestMessage = MakeApiMessage({
      url,
      method: "GET",
    });

    this.getProductsApiCallId = requestMessage.messageId;
    this.isLoadingMoreProducts = pageNo > 1;
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  getProductsByItemType = (itemType: string, sectionIds: number[] = [], pageNo: number = 1, searchQuery: string = "") => {
    // Don't load products if no sections selected
    if (sectionIds.length === 0 && !this.state.isSectionSelectAll) {
      return;
    }

    // Build filter query based on item type
    let url = "";
    if (itemType === "parent_product") {
      url = "/bx_block_catalogue/catalogues/parent_products_dropdown";
    } else {
      url = "/bx_block_catalogue/catalogues?dropdown=true";
    }
    
    // Add section filter if sections are selected
    if (sectionIds.length > 0 && !this.state.isSectionSelectAll) {
      const sectionFilter = sectionIds.join(",");
      const separator = itemType === "parent_product" ? "?" : "&";
      url += `${separator}filter_by[section_ids]=${sectionFilter}`;
    }
    
    // Add search query if provided
    if (searchQuery) {
      const separator = url.includes("?") ? "&" : "?";
      url += `${separator}filter_by[query]=${searchQuery}`;
    }
    
    // Add pagination
    const separator = url.includes("?") ? "&" : "?";
    url += `${separator}page_no=${pageNo}&per_page=20`;

    const requestMessage = MakeApiMessage({
      url,
      method: "GET",
    });

    this.getProductsApiCallId = requestMessage.messageId;
    this.isLoadingMoreProducts = pageNo > 1;
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  getPromotionItemProducts = (itemType: string, sectionIds: number[] = [], pageNo: number = 1, searchQuery: string = "") => {
    // Don't load products if no sections selected
    if (sectionIds.length === 0 && !this.state.isSectionSelectAll) {
      return;
    }

    // Build filter query based on item type
    let url = "";
    if (itemType === "parent_product") {
      url = "/bx_block_catalogue/catalogues/parent_products_dropdown";
    } else {
      url = "/bx_block_catalogue/catalogues?dropdown=true";
    }
    
    // Add section filter if sections are selected
    if (sectionIds.length > 0 && !this.state.isSectionSelectAll) {
      const sectionFilter = sectionIds.join(",");
      const separator = itemType === "parent_product" ? "?" : "&";
      url += `${separator}filter_by[section_ids]=${sectionFilter}`;
    }
    
    // Add search query if provided
    if (searchQuery) {
      const separator = url.includes("?") ? "&" : "?";
      url += `${separator}filter_by[query]=${searchQuery}`;
    }
    
    // Add pagination
    const separator = url.includes("?") ? "&" : "?";
    url += `${separator}page_no=${pageNo}&per_page=20`;

    const requestMessage = MakeApiMessage({
      url,
      method: "GET",
    });

    this.getPromotionItemProductsApiCallId = requestMessage.messageId;
    this.isLoadingMorePromotionItemProducts = pageNo > 1;
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  getOfferItemProducts = (itemType: string, sectionIds: number[] = [], pageNo: number = 1, searchQuery: string = "") => {
    // Don't load products if no sections selected
    if (sectionIds.length === 0 && !this.state.isSectionSelectAll) {
      return;
    }

    // Build filter query based on item type
    let url = "";
    if (itemType === "parent_product") {
      url = "/bx_block_catalogue/catalogues/parent_products_dropdown";
    } else {
      url = "/bx_block_catalogue/catalogues?dropdown=true";
    }
    
    // Add section filter if sections are selected
    if (sectionIds.length > 0 && !this.state.isSectionSelectAll) {
      const sectionFilter = sectionIds.join(",");
      const separator = itemType === "parent_product" ? "?" : "&";
      url += `${separator}filter_by[section_ids]=${sectionFilter}`;
    }
    
    // Add search query if provided
    if (searchQuery) {
      const separator = url.includes("?") ? "&" : "?";
      url += `${separator}filter_by[query]=${searchQuery}`;
    }
    
    // Add pagination
    const separator = url.includes("?") ? "&" : "?";
    url += `${separator}page_no=${pageNo}&per_page=20`;

    const requestMessage = MakeApiMessage({
      url,
      method: "GET",
    });

    this.getOfferItemProductsApiCallId = requestMessage.messageId;
    this.isLoadingMoreOfferItemProducts = pageNo > 1;
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  getPlatforms = () => {
    const requestMessage = MakeApiMessage({
      url: "/bx_block_promo_codes/promo_codes/platform_listing",
      method: "GET",
    });

    this.getPlatformsApiCallId = requestMessage.messageId;
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  getStores = (areaIds: number[]) => {
    if (areaIds.length === 0) {
      this.setState({ storeList: [], selectedStores: [] });
      return;
    }

    const areaQuery = areaIds.map(id => `area_id[]=${id}`).join("&");
    const requestMessage = MakeApiMessage({
      url: `/bx_block_store_management/store_managements?${areaQuery}&dropdown=true&allow_access=true`,
      method: "GET",
    });

    this.getStoresApiCallId = requestMessage.messageId;
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  getExcludeDays = () => {
    const requestMessage = MakeApiMessage({
      url: "/bx_block_promo_codes/promo_codes/exclude_days_listing",
      method: "GET",
    });

    this.getExcludeDaysApiCallId = requestMessage.messageId;
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  handlePromotionTypesResponse = (responseJson: any) => {
    if (responseJson && Array.isArray(responseJson)) {
      const promotionTypes = responseJson.map((item: any) => ({
        id: item.id,
        name: item.display_name || item.name,
        label: item.display_name || item.name
      }));
      this.setState({ promotionTypes });
    }
  };

  handleRegionsResponse = (responseJson: any) => {
    if (responseJson && responseJson.data) {
      const regionList = responseJson.data.map((item: any) => ({
        id: item.id,
        name: item.attributes.region_name || item.attributes.name || item.attributes.branch_name || item.attributes.name_translation,
        option: item.attributes.region_name || item.attributes.name || item.attributes.branch_name || item.attributes.name_translation
      }));
      
      this.setState({ 
        regionList
      });
    }
  };

  handleAreasResponse = (responseJson: any) => {
    if (responseJson && responseJson.data) {
      const areaList = responseJson.data.map((item: any) => ({
        id: item.id,
        name: item.attributes.area_name || item.attributes.name || item.attributes.name_translation,
        option: item.attributes.area_name || item.attributes.name || item.attributes.name_translation
      }));
      
      // In edit mode, restore the saved area selections
      const shouldRestoreAreaData = this.isEdit && this.tempAreaDataForEdit.length > 0;
      const areaDataToUse = shouldRestoreAreaData ? this.tempAreaDataForEdit : [];
      
      this.setState({ 
        areaList,
        areaSelectedData: areaDataToUse,
        selectedAreasForList: areaDataToUse,
        isAreaSelectAll: false,
        selectedAreas: shouldRestoreAreaData ? this.tempAreaDataForEdit : []
      });
      
      // Clear the temp data after using it
      if (shouldRestoreAreaData) {
        this.tempAreaDataForEdit = [];
      }
    }
  };

  handleSectionsResponse = (responseJson: any) => {
    if (responseJson && responseJson.data) {
      const sectionList = responseJson.data.map((item: any) => ({
        id: item.id,
        name: item.attributes.section_name || item.attributes.name,
        option: item.attributes.section_name || item.attributes.name
      }));
      
      // Initialize sectionLists for the multi-select pattern
      const sectionLists = sectionList.map((item: any) => ({
        id: item.id,
        option: item.name
      }));
      
      this.setState({ 
        sectionList,
        sectionLists
      });
    }
  };

  handleServicesResponse = (responseJson: any) => {
    if (responseJson && responseJson.data) {
      const serviceList = responseJson.data.map((item: any) => ({
        id: item.id,
        name: item.attributes?.service_name || item.attributes?.name,
        option: item.attributes?.service_name || item.attributes?.name,
        label: item.attributes?.service_name || item.attributes?.name
      }));
      
      // Initialize serviceOptions for the multi-select pattern
      const serviceOptions = serviceList.map((item: any) => ({
        id: item.id,
        option: item.option || item.name
      }));
      
      this.setState({ 
        serviceList,
        serviceOptions
      });
    }
  };

  handleProductsResponse = (responseJson: any) => {
    if (responseJson && responseJson.data) {
      const newProducts = responseJson.data.map((item: any) => ({
        id: item.id || item.attributes?.id,
        name: item.attributes?.product_name || item.attributes?.name || item.attributes?.catalogue_name,
        option: item.attributes?.product_name || item.attributes?.name || item.attributes?.catalogue_name,
        label: item.attributes?.product_name || item.attributes?.name || item.attributes?.catalogue_name
      }));
      
      // Handle pagination - append or replace based on page number
      let productList: any[];
      let productListItemsDropdown: any[];
      
      if (this.productNextPage === 1) {
        // First page - replace existing products
        productList = newProducts;
        productListItemsDropdown = newProducts.map((item: any) => ({
          id: item.id,
          option: item.option
        }));
      } else {
        // Subsequent pages - append to existing products
        productList = [...this.state.productList, ...newProducts];
        productListItemsDropdown = [
          ...this.state.productListItemsDropdown,
          ...newProducts.map((item: any) => ({
            id: item.id,
            option: item.option
          }))
        ];
      }
      
      // Check if there are more products to load
      const hasMoreProducts = newProducts.length === 20; // Assuming page size is 20
      
      this.setState({ 
        productList,
        productListItemsDropdown
      });
      
      // Update pagination state
      this.hasMoreProducts = hasMoreProducts;
      this.isLoadingMoreProducts = false;
      if (hasMoreProducts) {
        this.productNextPage += 1;
      }
    } else {
      this.isLoadingMoreProducts = false;
      this.hasMoreProducts = false;
    }
  };

  handlePromotionItemProductsResponse = (responseJson: any) => {
    if (responseJson && responseJson.data) {
      const newProducts = responseJson.data.map((item: any) => ({
        id: item.id || item.attributes?.id,
        name: item.attributes?.product_name || item.attributes?.name || item.attributes?.catalogue_name,
        option: item.attributes?.product_name || item.attributes?.name || item.attributes?.catalogue_name,
        label: item.attributes?.product_name || item.attributes?.name || item.attributes?.catalogue_name
      }));
      
      // Handle pagination - append or replace based on page number
      let promotionItemProductListDropdown: any[];
      
      if (this.promotionItemProductNextPage === 1) {
        // First page - merge with existing selected products (for edit mode)
        const existingSelectedProducts = this.state.promotionItemProductListDropdown.filter((existing: any) => 
          !newProducts.some((newItem: any) => newItem.id === existing.id)
        );
        
        promotionItemProductListDropdown = [
          ...existingSelectedProducts,
          ...newProducts.map((item: any) => ({
            id: item.id,
            option: item.option
          }))
        ];
      } else {
        // Subsequent pages - append to existing products
        promotionItemProductListDropdown = [
          ...this.state.promotionItemProductListDropdown,
          ...newProducts.map((item: any) => ({
            id: item.id,
            option: item.option
          }))
        ];
      }
      
      // Check if there are more products to load
      const hasMoreProducts = newProducts.length === 20; // Assuming page size is 20
      
      this.setState({ 
        promotionItemProductListDropdown
      });
      
      // Update pagination state
      this.hasMorePromotionItemProducts = hasMoreProducts;
      this.isLoadingMorePromotionItemProducts = false;
      if (hasMoreProducts) {
        this.promotionItemProductNextPage += 1;
      }
    } else {
      this.isLoadingMorePromotionItemProducts = false;
      this.hasMorePromotionItemProducts = false;
    }
  };

  handleOfferItemProductsResponse = (responseJson: any) => {
    if (responseJson && responseJson.data) {
      const newProducts = responseJson.data.map((item: any) => ({
        id: item.id || item.attributes?.id,
        name: item.attributes?.product_name || item.attributes?.name || item.attributes?.catalogue_name,
        option: item.attributes?.product_name || item.attributes?.name || item.attributes?.catalogue_name,
        label: item.attributes?.product_name || item.attributes?.name || item.attributes?.catalogue_name
      }));
      
      // Handle pagination - append or replace based on page number
      let offerItemProductListDropdown: any[];
      
      if (this.offerItemProductNextPage === 1) {
        // First page - merge with existing selected products (for edit mode)
        const existingSelectedProducts = this.state.offerItemProductListDropdown.filter((existing: any) => 
          !newProducts.some((newItem: any) => newItem.id === existing.id)
        );
        
        offerItemProductListDropdown = [
          ...existingSelectedProducts,
          ...newProducts.map((item: any) => ({
            id: item.id,
            option: item.option
          }))
        ];
      } else {
        // Subsequent pages - append to existing products
        offerItemProductListDropdown = [
          ...this.state.offerItemProductListDropdown,
          ...newProducts.map((item: any) => ({
            id: item.id,
            option: item.option
          }))
        ];
      }
      
      // Check if there are more products to load
      const hasMoreProducts = newProducts.length === 20; // Assuming page size is 20
      
      this.setState({ 
        offerItemProductListDropdown
      });
      
      // Update pagination state
      this.hasMoreOfferItemProducts = hasMoreProducts;
      this.isLoadingMoreOfferItemProducts = false;
      if (hasMoreProducts) {
        this.offerItemProductNextPage += 1;
      }
    } else {
      this.isLoadingMoreOfferItemProducts = false;
      this.hasMoreOfferItemProducts = false;
    }
  };

  handlePlatformsResponse = (responseJson: any) => {
    if (responseJson && responseJson.data) {
      const platformList = responseJson.data.map((item: any) => ({
        id: item.id || item.attributes?.id,
        name: item.attributes?.platform_name || item.attributes?.name,
        option: item.attributes?.platform_name || item.attributes?.name,
        label: item.attributes?.platform_name || item.attributes?.name
      }));
      this.setState({ platformList });
    }
  };

  handleStoresResponse = (responseJson: any) => {
    if (responseJson && responseJson.data) {
      const storeList = responseJson.data.map((item: any) => ({
        id: item.id,
        name: item.attributes.store_name || item.attributes.name,
        option: item.attributes.store_name || item.attributes.name,
        store_name: item.attributes.store_name
      }));
      
      // In edit mode, restore the saved store selections
      const shouldRestoreStoreData = this.isEdit && this.tempStoreDataForEdit.length > 0;
      const storeDataToUse = shouldRestoreStoreData ? this.tempStoreDataForEdit : [];
      
      this.setState({ 
        storeList,
        selectedStoresForList: storeDataToUse
      });
      
      // Clear the temp data after using it
      if (shouldRestoreStoreData) {
        this.tempStoreDataForEdit = [];
      }
    }
  };

  handleExcludeDaysResponse = (responseJson: any) => {
    if (responseJson && responseJson.data) {
      const excludeDayList = responseJson.data.map((item: any) => ({
        id: item.id,
        name: item.attributes.day,
        option: item.attributes.day
      }));
      this.setState({ excludeDayList });
    }
  };

  handleGetPromotionResponse = (responseJson: any) => {
    if (responseJson && responseJson.data) {
      const promotion = responseJson.data;
      
      // Map the promotion data to form state format
      const regionData = promotion.attributes.regions?.map((r: any) => ({ id: r.id.toString(), option: r.name })) || [];
      const areaData = promotion.attributes.areas?.map((a: any) => ({ id: a.id.toString(), option: a.name })) || [];
      const sectionData = promotion.attributes.sections?.map((s: any) => ({ id: s.id.toString(), option: s.name })) || [];
      const serviceData = promotion.attributes.services?.map((s: any) => ({ id: s.id.toString(), option: s.name })) || [];
      const platformData = promotion.attributes.platforms?.map((p: any) => ({ id: p.id.toString(), option: p.name })) || [];
      const storeData = promotion.attributes.stores?.map((s: any) => ({ id: s.id.toString(), option: s.name })) || [];
      
      this.setState({
        data: promotion,
        showEndDate: !!promotion.attributes.end_date,
        
        // Legacy state variables (for backward compatibility)
        selectedRegions: promotion.attributes.regions || [],
        selectedAreas: promotion.attributes.areas || [],
        selectedSections: promotion.attributes.sections || [],
        selectedProducts: promotion.attributes.products || [],
        selectedPlatforms: promotion.attributes.platforms || [],
        selectedStores: promotion.attributes.stores || [],
        
        // New multi-select state variables (for UI)
        regionSelectedData: regionData,
        selectedRegionsForList: regionData,
        areaSelectedData: areaData,
        selectedAreasForList: areaData,
        sectionSelectedData: sectionData,
        selectedSectionsForList: sectionData,
        sectionLists: sectionData,
        selectedServices: serviceData,
        selectedServicesForList: serviceData,
        serviceOptions: serviceData,
        selectedPlatform: platformData,
        platformSelectedOptionForList: platformData,
        selectedStoresForList: storeData
      });
      
      // Load dependent data after setting initial selections
      if (regionData.length > 0) {
        const regionIds = regionData.map((r: any) => Number(r.id));
        // Store the area data to restore after areas are loaded
        this.tempAreaDataForEdit = areaData;
        this.getAreas(regionIds);
      }
      
      if (areaData.length > 0) {
        const areaIds = areaData.map((a: any) => Number(a.id));
        // Store the store data to restore after stores are loaded  
        this.tempStoreDataForEdit = storeData;
        this.getStores(areaIds);
      }
      
      if (sectionData.length > 0) {
        const sectionIds = sectionData.map((s: any) => Number(s.id));
        this.getProducts(sectionIds);
        
        // Also load products for promotion items if they exist
        if (promotion.attributes.promotion_items?.length > 0) {
          const firstPromotionItem = promotion.attributes.promotion_items[0];
          if (firstPromotionItem.item_type && firstPromotionItem.item_type !== "all_products") {
            this.getPromotionItemProducts(firstPromotionItem.item_type, sectionIds);
          }
        }
        
        // Also load products for offer items if they exist
        if (promotion.attributes.promotion_offer_items?.length > 0) {
          const firstOfferItem = promotion.attributes.promotion_offer_items[0];
          if (!["same_item", "all_products"].includes(firstOfferItem.offer_type) && firstOfferItem.item_type) {
            this.getOfferItemProducts(firstOfferItem.item_type, sectionIds);
          }
        }
      }
      
      // Create dropdown options for existing selected products
      const promotionItemProducts = promotion.attributes.promotion_items?.filter((item: any) => item.item_type !== "all_products").map((item: any) => ({
        id: item.catalogue_id,
        option: item.product_name || "Product"
      })).filter((item: any) => item.id) || [];
      
      const offerItemProducts = promotion.attributes.promotion_offer_items?.map((item: any) => ({
        id: item.catalogue_id,
        option: item.product_name || "Product"
      })).filter((item: any) => item.id) || [];
      
      // Update form data with promotion items and offer items
      setTimeout(() => {
        const updatedData = {
          ...promotion,
          attributes: {
            ...promotion.attributes,
            // Extract promotion_type_id from the nested promotion_type object
            promotion_type_id: promotion.attributes.promotion_type?.id || null,
            promotion_items: promotion.attributes.promotion_items || [{ item_type: "single_product", catalogue_id: null }],
            promotion_offer_items: promotion.attributes.promotion_offer_items || [{ offer_type: "same_item" }],
            excluded_days: promotion.attributes.excluded_days?.map((day: any) => ({
              day_of_week: day.day_of_week
            })) || [],
            excluded_dates: promotion.attributes.excluded_dates || [],
            excluded_times: promotion.attributes.excluded_times || []
          }
        };
        
        this.setState({ 
          data: updatedData,
          // Populate product dropdowns with existing selections
          promotionItemProductListDropdown: promotionItemProducts,
          offerItemProductListDropdown: offerItemProducts
        });
      }, 1000); // Give time for dependent dropdowns to load
    }
  };

  handleCreatePromotionResponse = (responseJson: any) => {
    this.setState({ isSaveLoader: false });
    
    if (responseJson && responseJson.data) {
      this.setState({ 
        saveStatus: "Promotion saved successfully"
      });
      setTimeout(() => {
        this.props.navigation.navigate("Promotions");
      }, 2000);
    } else if (responseJson && responseJson.errors) {
      this.setState({ 
        saveStatus: responseJson.errors,
        error: this.parseErrors(responseJson.errors)
      });
    }
  };

  handleUpdatePromotionResponse = (responseJson: any) => {
    this.setState({ isSaveLoader: false });
    
    if (responseJson && responseJson.data) {
      this.setState({ 
        saveStatus: "Promotion saved successfully"
      });
      setTimeout(() => {
        this.props.navigation.navigate("Promotions");
      }, 2000);
    } else if (responseJson && responseJson.errors) {
      this.setState({ 
        saveStatus: responseJson.errors,
        error: this.parseErrors(responseJson.errors)
      });
    }
  };

  parseErrors = (errors: any) => {
    if (typeof errors === "string") {
      return { general: errors };
    }
    return errors;
  };


  onChangeValue = (field: string, value: any) => {
    const { data } = this.state;
    const updatedData = {
      ...data,
      attributes: {
        ...data.attributes,
        [field]: value
      }
    };
    
    this.setState({ data: updatedData });
    
    // Handle dependent dropdowns - but skip for region_ids as it's handled in handleRegionMultiSelect
    if (field === "area_ids") {
      // Filter out "All" values and convert to numbers for API call
      const areaIds = Array.isArray(value) ? value.filter((id: any) => id !== "All").map((id: any) => Number(id)) : [];
      if (areaIds.length > 0) {
        this.getStores(areaIds);
      } else {
        this.setState({ storeList: [], selectedStores: [] });
      }
    }
  };

  validateForm = () => {
    const { data } = this.state;
    const errors: any = {};
    
    // Required field validations
    if (!data.attributes.promotion_name || data.attributes.promotion_name.trim() === "") {
      errors.promotion_name = "Promotion name is required";
    }
    
    if (!data.attributes.promo_item_text_en || data.attributes.promo_item_text_en.trim() === "") {
      errors.promo_item_text_en = "Promo item text (English) is required";
    }
    
    if (!data.attributes.promo_item_text_ar || data.attributes.promo_item_text_ar.trim() === "") {
      errors.promo_item_text_ar = "Promo item text (Arabic) is required";
    }
    
    if (!data.attributes.order_discount_name_en || data.attributes.order_discount_name_en.trim() === "") {
      errors.order_discount_name_en = "Order discount name (English) is required";
    }
    
    if (!data.attributes.order_discount_name_ar || data.attributes.order_discount_name_ar.trim() === "") {
      errors.order_discount_name_ar = "Order discount name (Arabic) is required";
    }
    
    // Region validation
    const hasRegions = this.state.isRegionSelectAll || 
      (this.state.regionSelectedData && this.state.regionSelectedData.length > 0 && 
       this.state.regionSelectedData.some((r: any) => r.id !== "All" && r.id !== "-1"));
    if (!hasRegions) {
      errors.region_ids = "At least one region is required";
    }
    
    // Area validation
    const hasAreas = this.state.isAreaSelectAll || 
      (this.state.areaSelectedData && this.state.areaSelectedData.length > 0 && 
       this.state.areaSelectedData.some((a: any) => a.id !== "All" && a.id !== "-1"));
    if (!hasAreas) {
      errors.area_ids = "At least one area is required";
    }
    
    // Section validation
    const hasSections = this.state.isSectionSelectAll || 
      (this.state.sectionSelectedData && this.state.sectionSelectedData.length > 0 && 
       this.state.sectionSelectedData.some((s: any) => s.id !== "All" && s.id !== "-1"));
    if (!hasSections) {
      errors.section_ids = "At least one section is required";
    }
    
    // Service validation
    const hasServices = this.state.isServiceSelectAll || 
      (this.state.selectedServices && this.state.selectedServices.length > 0 && 
       this.state.selectedServices.some((s: any) => s.id !== "All" && s.id !== "-1"));
    if (!hasServices) {
      errors.service_ids = "At least one service is required";
    }
    
    // Platform validation
    const hasPlatforms = this.state.isPlatformSelectAll || 
      (this.state.selectedPlatform && this.state.selectedPlatform.length > 0 && 
       this.state.selectedPlatform.some((p: any) => p.id !== "All" && p.id !== "-1"));
    if (!hasPlatforms) {
      errors.platform_ids = "At least one platform is required";
    }
    
    if (!data.attributes.start_date) {
      errors.start_date = "Start date is required";
    }
    
    if (!data.attributes.status) {
      errors.status = "Status is required";
    }
    
    // Promotion items validation
    const validPromotionItems = data.attributes.promotion_items.filter(item => !item._destroy);
    if (validPromotionItems.length === 0) {
      errors.promotion_items = "At least one promotion item is required";
    } else {
      // Validate each promotion item has a product selected
      const hasInvalidPromotionItem = validPromotionItems.some(item => item.item_type !== "all_products" && !item.catalogue_id);
      if (hasInvalidPromotionItem) {
        errors.promotion_items = "All promotion items must have a product selected";
      }
    }
    
    // Promotion offer items validation
    const validOfferItems = data.attributes.promotion_offer_items.filter(item => !item._destroy);
    if (validOfferItems.length === 0) {
      errors.promotion_offer_items = "At least one promotion offer item is required";
    } else {
      // Validate different_item type offer items have a product selected
      const hasInvalidOfferItem = validOfferItems.some(item => 
        item.offer_type === "different_item" && !item.catalogue_id
      );
      if (hasInvalidOfferItem) {
        errors.promotion_offer_items = "Offer items with 'Different Specific Item' must have a product selected";
      }
    }
    
    if (!data.attributes.promotion_type_id) {
      errors.promotion_type_id = "Promotion type is required";
    }
    
    if (data.attributes.used === "specific_number_of_orders" && !data.attributes.no_of_orders) {
      errors.no_of_orders = "Number of orders is required";
    }
    
    if (this.state.showEndDate && data.attributes.end_date && data.attributes.start_date) {
      if (new Date(data.attributes.end_date) <= new Date(data.attributes.start_date)) {
        errors.end_date = "End date must be after start date";
      }
    }
    
    return errors;
  };

  onSubmit = () => {
    const errors = this.validateForm();
    
    if (Object.keys(errors).length > 0) {
      this.setState({ error: errors });
      return;
    }
    
    this.setState({ isSaveLoader: true, error: {} });
    
    const { data } = this.state;
    
    // Prepare the request data  
    const requestData = {
      data: {
        ...data.attributes,
        // Convert numeric fields to numbers
        minimum_order_amount: data.attributes.minimum_order_amount ? Number(data.attributes.minimum_order_amount) : 0,
        no_of_orders: data.attributes.no_of_orders ? Number(data.attributes.no_of_orders) : null,
        minimum_items: data.attributes.minimum_items ? Number(data.attributes.minimum_items) : 1,
        section_ids: this.state.isSectionSelectAll ? 
          this.state.sectionList.map((s: any) => Number(s.id)) : 
          this.state.sectionSelectedData.filter((s: any) => s.id !== "All" && s.id !== "-1").map((s: any) => Number(s.id)),
        product_ids: [],
        service_ids: this.state.isServiceSelectAll ? 
          this.state.serviceList.map((s: any) => s.id) : 
          this.state.selectedServices.filter((s: any) => s.id !== "All" && s.id !== "-1").map((s: any) => s.id),
        platform_ids: this.state.isPlatformSelectAll ? 
          this.state.platformList.map((p: any) => p.id) : 
          this.state.selectedPlatform.filter((p: any) => p.id !== "All" && p.id !== "-1").map((p: any) => Number(p.id)),
        region_ids: this.state.isRegionSelectAll ? 
          this.state.regionList.map((r: any) => Number(r.id)) : 
          this.state.regionSelectedData.filter((r: any) => r.id !== "All" && r.id !== "-1").map((r: any) => Number(r.id)),
        area_ids: this.state.isAreaSelectAll ? 
          this.state.areaList.map((a: any) => Number(a.id)) : 
          this.state.areaSelectedData.filter((a: any) => a.id !== "All" && a.id !== "-1").map((a: any) => Number(a.id)),
        store_ids: this.state.isStoreSelectAll ? 
          this.state.storeList.map((s: any) => s.id) : 
          this.state.selectedStoresForList.filter((s: any) => s.id !== "All" && s.id !== "-1").map((s: any) => Number(s.id)),
        promotion_items_attributes: data.attributes.promotion_items.filter(item => !item._destroy).map(item =>
          item.item_type === "all_products" ? { ...item, catalogue_id: null } : item
        ),
        promotion_offer_items_attributes: data.attributes.promotion_offer_items.filter(item => !item._destroy).map(item => {
          // Ensure item_type is included for different_item offers
          if (item.offer_type === "different_item" && !item.item_type) {
            return { ...item, item_type: "single_product" };
          }
          // Remove item_type for same_item offers (it's not needed)
          if (item.offer_type === "same_item") {
            const { item_type, ...restItem } = item;
            return restItem;
          }
          // All products offers have no item type or product
          if (item.offer_type === "all_products") {
            const { item_type, ...restItem } = item;
            return { ...restItem, catalogue_id: null };
          }
          return item;
        }),
        promotion_excluded_days_attributes: data.attributes.excluded_days.filter(item => !item._destroy).map(item => ({
          ...item,
          day_of_week: item.day_of_week ? item.day_of_week.toLowerCase() : item.day_of_week
        })),
        promotion_excluded_dates_attributes: data.attributes.excluded_dates.filter(item => !item._destroy),
        promotion_excluded_times_attributes: data.attributes.excluded_times.filter(item => !item._destroy)
      }
    };
    
    let requestMessage;
    
    if (this.isEdit) {
      requestMessage = MakeApiMessage({
        url: `${configJSON.ApiUrls.promotionUpdate}${this.promotionId}`,
        method: "PUT",
        body: JSON.stringify(requestData),
      });
      this.updatePromotionApiCallId = requestMessage.messageId;
    } else {
      requestMessage = MakeApiMessage({
        url: configJSON.ApiUrls.promotionCreate,
        method: "POST",
        body: JSON.stringify(requestData),
      });
      this.createPromotionApiCallId = requestMessage.messageId;
    }

    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  clickCancel = () => {
    this.props.navigation.navigate("Promotions");
  };

  // Dynamic field handlers
  addPromotionItem = () => {
    const { data } = this.state;
    const updatedData = {
      ...data,
      attributes: {
        ...data.attributes,
        promotion_items: [
          ...data.attributes.promotion_items,
          { item_type: "single_product", catalogue_id: null }
        ]
      }
    };
    this.setState({ data: updatedData }, () => {
      // Load products for the new item if sections are selected
      const selectedSectionIds = this.state.sectionSelectedData
        .filter((s: any) => s.id !== "-1" && s.id !== "All")
        .map((s: any) => Number(s.id));
      
      if ((selectedSectionIds.length > 0 || this.state.isSectionSelectAll) && this.state.promotionItemProductListDropdown.length === 0) {
        this.promotionItemProductNextPage = 1;
        this.promotionItemProductSearchQuery = "";
        this.hasMorePromotionItemProducts = true;
        this.getPromotionItemProducts("single_product", selectedSectionIds, 1, "");
      }
    });
  };

  removePromotionItem = (index: number) => {
    const { data } = this.state;
    const items = [...data.attributes.promotion_items];
    if (items[index].id) {
      items[index]._destroy = true;
    } else {
      items.splice(index, 1);
    }
    
    const updatedData = {
      ...data,
      attributes: {
        ...data.attributes,
        promotion_items: items
      }
    };
    this.setState({ data: updatedData });
  };

  updatePromotionItem = (index: number, field: string, value: any) => {
    const { data } = this.state;
    const items = [...data.attributes.promotion_items];
    const oldItemType = items[index].item_type;
    items[index] = { ...items[index], [field]: value };

    // All products on the promotion side means all products on the offer side too
    let offerItems = data.attributes.promotion_offer_items;
    if (field === "item_type" && oldItemType !== value) {
      if (value === "all_products") {
        offerItems = offerItems.map(offer => {
          const { item_type, ...restOffer } = offer;
          return { ...restOffer, offer_type: "all_products", catalogue_id: null };
        });
      } else if (oldItemType === "all_products") {
        offerItems = offerItems.map(offer => {
          if (offer.offer_type !== "all_products") return offer;
          const { item_type, ...restOffer } = offer;
          return { ...restOffer, offer_type: "same_item", catalogue_id: null };
        });
      }
    }
    
    const updatedData = {
      ...data,
      attributes: {
        ...data.attributes,
        promotion_items: items,
        promotion_offer_items: offerItems
      }
    };
    this.setState({ data: updatedData }, () => {
      // If item_type changed, clear the selected product and reload products for this type
      if (field === "item_type" && oldItemType !== value) {
        // Clear the catalogue_id for this item
        items[index].catalogue_id = null;
        const finalData = {
          ...updatedData,
          attributes: {
            ...updatedData.attributes,
            promotion_items: items
          }
        };
        // Clear the promotion item product dropdown before loading new products
        this.setState({ 
          data: finalData,
          promotionItemProductListDropdown: [] 
        });
        
        // Reload products for the new item type
        const selectedSectionIds = this.state.sectionSelectedData
          .filter((s: any) => s.id !== "-1" && s.id !== "All")
          .map((s: any) => Number(s.id));
        
        if (value !== "all_products" && (selectedSectionIds.length > 0 || this.state.isSectionSelectAll)) {
          this.promotionItemProductNextPage = 1;
          this.promotionItemProductSearchQuery = "";
          this.hasMorePromotionItemProducts = true;
          this.getPromotionItemProducts(value, selectedSectionIds, 1, "");
        }
      }
    });
  };

  addPromotionOfferItem = () => {
    const { data } = this.state;
    const updatedData = {
      ...data,
      attributes: {
        ...data.attributes,
        promotion_offer_items: [
          ...data.attributes.promotion_offer_items,
          this.isAllProductsPromotion()
            ? { offer_type: "all_products" }
            : { offer_type: "same_item", item_type: "single_product" }
        ]
      }
    };
    this.setState({ data: updatedData }, () => {
      // Load products for the new offer item if sections are selected
      const selectedSectionIds = this.state.sectionSelectedData
        .filter((s: any) => s.id !== "-1" && s.id !== "All")
        .map((s: any) => Number(s.id));
      
      if ((selectedSectionIds.length > 0 || this.state.isSectionSelectAll) && this.state.offerItemProductListDropdown.length === 0) {
        this.offerItemProductNextPage = 1;
        this.offerItemProductSearchQuery = "";
        this.hasMoreOfferItemProducts = true;
        this.getOfferItemProducts("single_product", selectedSectionIds, 1, "");
      }
    });
  };

  removePromotionOfferItem = (index: number) => {
    const { data } = this.state;
    const items = [...data.attributes.promotion_offer_items];
    if (items[index].id) {
      items[index]._destroy = true;
    } else {
      items.splice(index, 1);
    }
    
    const updatedData = {
      ...data,
      attributes: {
        ...data.attributes,
        promotion_offer_items: items
      }
    };
    this.setState({ data: updatedData });
  };

  updatePromotionOfferItem = (index: number, field: string, value: any) => {
    const { data } = this.state;
    const items = [...data.attributes.promotion_offer_items];
    const oldItemType = items[index].item_type;
    const oldOfferType = items[index].offer_type;
    items[index] = { ...items[index], [field]: value };
    
    // When offer_type changes to "different_item", ensure item_type is set
    if (field === "offer_type" && value === "different_item") {
      if (!items[index].item_type) {
        items[index].item_type = "single_product"; // Default to single_product
      }
    }
    
    // When offer_type is "same_item", remove the item_type as it's not needed
    if (field === "offer_type" && value === "same_item") {
      delete items[index].item_type;
      delete items[index].catalogue_id; // Also clear the selected product
    }
    
    const updatedData = {
      ...data,
      attributes: {
        ...data.attributes,
        promotion_offer_items: items
      }
    };
    this.setState({ data: updatedData }, () => {
      // If item_type changed, clear the selected product and reload products for this type
      if (field === "item_type" && oldItemType !== value) {
        // Clear the catalogue_id for this item
        items[index].catalogue_id = null;
        const finalData = {
          ...updatedData,
          attributes: {
            ...updatedData.attributes,
            promotion_offer_items: items
          }
        };
        // Clear the offer item product dropdown before loading new products
        this.setState({ 
          data: finalData,
          offerItemProductListDropdown: []
        });
        
        // Reload products for the new item type
        const selectedSectionIds = this.state.sectionSelectedData
          .filter((s: any) => s.id !== "-1" && s.id !== "All")
          .map((s: any) => Number(s.id));
        
        if (selectedSectionIds.length > 0 || this.state.isSectionSelectAll) {
          this.offerItemProductNextPage = 1;
          this.offerItemProductSearchQuery = "";
          this.hasMoreOfferItemProducts = true;
          this.getOfferItemProducts(value, selectedSectionIds, 1, "");
        }
      }
      
      // If offer_type changed to "different_item", load products for the default item_type
      if (field === "offer_type" && value === "different_item" && oldOfferType !== "different_item") {
        const selectedSectionIds = this.state.sectionSelectedData
          .filter((s: any) => s.id !== "-1" && s.id !== "All")
          .map((s: any) => Number(s.id));
        
        if (selectedSectionIds.length > 0 || this.state.isSectionSelectAll) {
          this.offerItemProductNextPage = 1;
          this.offerItemProductSearchQuery = "";
          this.hasMoreOfferItemProducts = true;
          const itemTypeToLoad = items[index].item_type || "single_product";
          this.getOfferItemProducts(itemTypeToLoad, selectedSectionIds, 1, "");
        }
      }
    });
  };

  addExcludedDate = () => {
    const { data } = this.state;
    const updatedData = {
      ...data,
      attributes: {
        ...data.attributes,
        excluded_dates: [
          ...data.attributes.excluded_dates,
          { excluded_date: null, reason: "" }
        ]
      }
    };
    this.setState({ data: updatedData });
  };

  removeExcludedDate = (index: number) => {
    const { data } = this.state;
    const items = [...data.attributes.excluded_dates];
    if (items[index].id) {
      items[index]._destroy = true;
    } else {
      items.splice(index, 1);
    }
    
    const updatedData = {
      ...data,
      attributes: {
        ...data.attributes,
        excluded_dates: items
      }
    };
    this.setState({ data: updatedData });
  };

  updateExcludedDate = (index: number, field: string, value: any) => {
    const { data } = this.state;
    const items = [...data.attributes.excluded_dates];
    items[index] = { ...items[index], [field]: value };
    
    const updatedData = {
      ...data,
      attributes: {
        ...data.attributes,
        excluded_dates: items
      }
    };
    this.setState({ data: updatedData });
  };

  addExcludedTime = () => {
    const { data } = this.state;
    const updatedData = {
      ...data,
      attributes: {
        ...data.attributes,
        excluded_times: [
          ...data.attributes.excluded_times,
          { start_time: "", end_time: "" }
        ]
      }
    };
    this.setState({ data: updatedData });
  };

  removeExcludedTime = (index: number) => {
    const { data } = this.state;
    const items = [...data.attributes.excluded_times];
    if (items[index].id) {
      items[index]._destroy = true;
    } else {
      items.splice(index, 1);
    }
    
    const updatedData = {
      ...data,
      attributes: {
        ...data.attributes,
        excluded_times: items
      }
    };
    this.setState({ data: updatedData });
  };

  updateExcludedTime = (index: number, field: string, value: any) => {
    const { data } = this.state;
    const items = [...data.attributes.excluded_times];
    items[index] = { ...items[index], [field]: value };
    
    const updatedData = {
      ...data,
      attributes: {
        ...data.attributes,
        excluded_times: items
      }
    };
    this.setState({ data: updatedData });
  };

  handleRegionMultiSelect = (value: { id: string, option: string }[], option: { id: string, option: string }) => {
    let updateStateData: Partial<S> = {}
    if (option.id == "-1") {
      updateStateData = this.state.isRegionSelectAll ?
        { ...updateStateData, isRegionSelectAll: false, regionSelectedData: [], selectedRegionsForList: [], areaList: [], areaSelectedData: [], selectedAreasForList: [], selectedAreas: [] }
        :
        {
          ...updateStateData,
          isRegionSelectAll: true,
          regionSelectedData: [{ id: "-1", option: "Select All" }],
          selectedRegionsForList: [{ id: "-1", option: "Select All" }, ...this.state.regionList.map((item: any) => { return { id: item.id, option: item.option || item.name } })],
          areaSelectedData: [],  // Clear area selection when Select All regions
          selectedAreasForList: [],  // Clear area selection list
          selectedAreas: []  // Clear legacy area selection
        };

    } else {
      const isValueContained = value.some((item) => item.id == option.id)
      value = value.filter((item) => item.id !== "-1")
      const newItems = isValueContained ? value.filter((item) => item.id != option.id) : [...value, option]
      const regionUpdateData = this.updateMultiSelectData(
        newItems,
        this.state.regionList.map((item: any) => { return { id: item.id, option: item.option || item.name } }),
        'isRegionSelectAll',
        'regionSelectedData',
        'selectedRegionsForList',
      )
      // Clear area selections when regions change
      updateStateData = { 
        ...updateStateData, 
        ...regionUpdateData,
        areaSelectedData: [],
        selectedAreasForList: [],
        selectedAreas: []
      }
    }
    this.setState(updateStateData as Pick<S, keyof S>, () => {
      // Update the form field with the selected region IDs and get areas
      let regionIdsForForm: any[];
      let regionIdsForAPI: number[];
      
      if (this.state.isRegionSelectAll) {
        // When "Select All" is chosen, use all region IDs for API call
        regionIdsForForm = ["All"];
        regionIdsForAPI = this.state.regionList.map((item: any) => Number(item.id));
      } else {
        // When specific regions are selected
        const selectedRegionIds = this.state.regionSelectedData?.filter((item: any) => item.id !== "All").map((item: any) => Number(item.id)) || [];
        regionIdsForForm = selectedRegionIds;
        regionIdsForAPI = selectedRegionIds;
      }
      
      this.onChangeValue("region_ids", regionIdsForForm);
      
      // Call getAreas with the appropriate region IDs
      if (regionIdsForAPI.length > 0) {
        this.getAreas(regionIdsForAPI);
      } else {
        this.setState({ areaList: [], areaSelectedData: [], selectedAreasForList: [] });
      }
    })
  };

  updateMultiSelectData = (newItems: { id: string | number, option: string }[], totalList: { id: string | number, option: string }[], isSelectAll: string, selectedData: string, selectedForList: string) => {
    const updateStateData: { [key: string]: boolean | { id: string | number, option: string }[] } = {}
    updateStateData[isSelectAll] = false
    updateStateData[selectedData] = newItems
    updateStateData[selectedForList] = newItems
    if (newItems.length === totalList.length) {
      updateStateData[isSelectAll] = true
      updateStateData[selectedData] = [{ id: "-1", option: "Select All" }]
      updateStateData[selectedForList] = [{ id: "-1", option: "Select All" }, ...totalList]
    }
    return updateStateData
  };

  handleAreaMultiSelect = (value: { id: string, option: string }[], option: { id: string, option: string }) => {
    let updateStateData: Partial<S> = {}
    if (option.id == "-1") {
      updateStateData = this.state.isAreaSelectAll ?
        { ...updateStateData, isAreaSelectAll: false, areaSelectedData: [], selectedAreasForList: [] }
        :
        {
          ...updateStateData,
          isAreaSelectAll: true,
          areaSelectedData: [{ id: "-1", option: "Select All" }],
          selectedAreasForList: [{ id: "-1", option: "Select All" }, ...this.state.areaList.map((item: any) => { return { id: item.id, option: item.option || item.name } })]
        };

    } else {
      const isValueContained = value.some((item) => item.id == option.id)
      value = value.filter((item) => item.id !== "-1")
      const newItems = isValueContained ? value.filter((item) => item.id != option.id) : [...value, option]
      const areaUpdateData = this.updateMultiSelectData(
        newItems,
        this.state.areaList.map((item: any) => { return { id: item.id, option: item.option || item.name } }),
        'isAreaSelectAll',
        'areaSelectedData',
        'selectedAreasForList',
      )
      updateStateData = { ...updateStateData, ...areaUpdateData }
    }
    this.setState(updateStateData as Pick<S, keyof S>, () => {
      // Update the form field with the selected area IDs and get stores
      let areaIdsForForm: any[];
      let areaIdsForAPI: number[];
      
      if (this.state.isAreaSelectAll) {
        // When \"Select All\" is chosen, use all area IDs for API call
        areaIdsForForm = ["All"];
        areaIdsForAPI = this.state.areaList.map((item: any) => Number(item.id));
      } else {
        // When specific areas are selected
        const selectedAreaIds = this.state.areaSelectedData?.filter((item: any) => item.id !== "All").map((item: any) => Number(item.id)) || [];
        areaIdsForForm = selectedAreaIds;
        areaIdsForAPI = selectedAreaIds;
      }
      
      this.onChangeValue("area_ids", areaIdsForForm);
      
      // Get stores for selected areas
      if (areaIdsForAPI.length > 0) {
        this.getStores(areaIdsForAPI);
      } else {
        this.setState({ storeList: [], selectedStores: [] });
      }
    })
  };

  handleSectionMultiSelect = (value: { id: string, option: string }[], option: { id: string, option: string }) => {
    let updateStateData: Partial<S> = {}
    if (option.id == "-1") {
      updateStateData = this.state.isSectionSelectAll ?
        { ...updateStateData, isSectionSelectAll: false, sectionSelectedData: [], selectedSectionsForList: [] }
        :
        {
          ...updateStateData,
          isSectionSelectAll: true,
          sectionSelectedData: [{ id: "All", option: "Select All" }],
          selectedSectionsForList: [{ id: "-1", option: "Select All" }, ...this.state.sectionLists]
        };

    } else {
      const isValueContained = value.some((item) => item.id == option.id)
      value = value.filter((item) => item.id !== "-1")
      const newItems = isValueContained ? value.filter((item) => item.id != option.id) : [...value, option]
      const sectionUpdateData = this.updateMultiSelectData(
        newItems,
        this.state.sectionLists,
        'isSectionSelectAll',
        'sectionSelectedData',
        'selectedSectionsForList',
      )
      updateStateData = { ...updateStateData, ...sectionUpdateData }
    }
    this.setState(updateStateData as Pick<S, keyof S>, () => {
      // Update the form field with the selected section IDs
      const selectedSectionIds = this.state.sectionSelectedData?.filter((item: any) => item.id !== "All" && item.id !== "-1").map((item: any) => Number(item.id)) || [];
      const sectionIds = this.state.isSectionSelectAll ? ["All"] : selectedSectionIds;
      this.onChangeValue("section_ids", sectionIds);
      
      // Reset product pagination and load products for selected sections
      this.productNextPage = 1;
      this.productSearchQuery = "";
      this.hasMoreProducts = true;
      
      // Load products based on selected sections
      if (selectedSectionIds.length > 0 || this.state.isSectionSelectAll) {
        this.getProducts(selectedSectionIds, 1, "");
        
        // Also load products for promotion items and offer items dropdowns
        // Reset pagination for both
        this.promotionItemProductNextPage = 1;
        this.promotionItemProductSearchQuery = "";
        this.hasMorePromotionItemProducts = true;
        
        this.offerItemProductNextPage = 1;
        this.offerItemProductSearchQuery = "";
        this.hasMoreOfferItemProducts = true;
        
        // Load products for both dropdowns with default "single_product" type
        this.getPromotionItemProducts("single_product", selectedSectionIds, 1, "");
        this.getOfferItemProducts("single_product", selectedSectionIds, 1, "");
      } else {
        // Clear products if no sections selected
        this.setState({ 
          productList: [], 
          productListItemsDropdown: [],
          promotionItemProductListDropdown: [],
          offerItemProductListDropdown: [],
          selectedProducts: [] 
        });
      }
    })
  };
  
  handleMultiSelectChange = (field: string, values: any[], option?: any) => {
    const stateField = field.replace("_ids", "");
    const selectedField = `selected${stateField.charAt(0).toUpperCase() + stateField.slice(1)}s`;
    
    // For products, platforms, stores, and excluded days, handle the multi-select properly
    if (field === 'product_ids') {
      this.handleProductMultiSelect(values, option);
    } else if (field === 'platform_ids') {
      this.handlePlatformMultiSelect(values, option);
    } else if (field === 'store_ids') {
      this.handleStoreMultiSelect(values, option);
    } else if (field === 'excluded_days') {
      this.handleExcludeDaysMultiSelect(values, option);
    } else {
      // Default handling for other fields
      const convertedValues = values.map((v: any) => ({
        id: v.id,
        name: v.option || v.name || '',
        label: v.option || v.label || v.name || '',
        option: v.option || v.name || ''
      }));
      
      this.setState({ [selectedField]: convertedValues } as any);
      
      // Update the data attribute IDs
      const ids = values.map((v: any) => Number(v.id));
      this.onChangeValue(`${field}`, ids);
    }
  };

  handleProductMultiSelect = (value: { id: string, option: string }[], option: { id: string, option: string }) => {
    // Handle product selection similar to promocodes
    const isValueContained = value.some((item) => item.id == option.id);
    const newItems = isValueContained 
      ? value.filter((item) => item.id != option.id) 
      : [...value, option];
    
    const selectedProducts = newItems.map(item => ({
      id: item.id,
      name: item.option,
      label: item.option,
      option: item.option
    }));
    
    this.setState({ 
      selectedProducts 
    }, () => {
      // Update the form field with selected product IDs
      const productIds = selectedProducts.map(p => Number(p.id));
      this.onChangeValue("product_ids", productIds);
    });
  };

  handlePlatformMultiSelect = (value: { id: string, option: string }[], option: { id: string, option: string }) => {
    let updateStateData: Partial<S> = {}
    if (option.id == "-1") {
      updateStateData = this.state.isPlatformSelectAll ?
        { ...updateStateData, isPlatformSelectAll: false, selectedPlatform: [], platformSelectedOptionForList: [] }
        :
        {
          ...updateStateData,
          isPlatformSelectAll: true,
          selectedPlatform: [{ id: "-1", option: "Select All" }],
          platformSelectedOptionForList: [{ id: "-1", option: "Select All" }, ...this.state.platformList.map((item: any) => { return { id: item.id, option: item.option || item.name } })]
        };

    } else {
      const isValueContained = value.some((item) => item.id == option.id)
      value = value.filter((item) => item.id !== "-1")
      const newItems = isValueContained ? value.filter((item) => item.id != option.id) : [...value, option]
      const platformUpdateData = this.updateMultiSelectData(
        newItems,
        this.state.platformList.map((item: any) => { return { id: item.id, option: item.option || item.name } }),
        'isPlatformSelectAll',
        'selectedPlatform',
        'platformSelectedOptionForList',
      )
      updateStateData = { ...updateStateData, ...platformUpdateData }
    }
    this.setState(updateStateData as Pick<S, keyof S>, () => {
      // Update the form field with the selected platform IDs
      let platformIdsForForm: any[];
      let platformIdsForAPI: number[];
      
      if (this.state.isPlatformSelectAll) {
        // When "Select All" is chosen, use all platform IDs for API call
        platformIdsForForm = ["All"];
        platformIdsForAPI = this.state.platformList.map((item: any) => Number(item.id));
      } else {
        // When specific platforms are selected
        const selectedPlatformIds = this.state.selectedPlatform?.filter((item: any) => item.id !== "All" && item.id !== "-1").map((item: any) => Number(item.id)) || [];
        platformIdsForForm = selectedPlatformIds;
        platformIdsForAPI = selectedPlatformIds;
      }
      
      // Update legacy selectedPlatforms for backward compatibility
      const selectedPlatforms = this.state.isPlatformSelectAll ? 
        this.state.platformList.map(item => ({
          id: item.id,
          name: item.name || item.option || '',
          label: item.name || item.option || '',
          option: item.name || item.option || ''
        })) :
        this.state.selectedPlatform?.filter(item => item.id !== "-1" && item.id !== "All").map(item => ({
          id: item.id,
          name: item.option || '',
          label: item.option || '',
          option: item.option || ''
        })) || [];
      
      this.setState({ selectedPlatforms });
      this.onChangeValue("platform_ids", platformIdsForForm);
      
      // Load stores if platform ID 1 (Store) is selected
      if (platformIdsForAPI.includes(1) && this.state.areaList.length > 0) {
        const areaIds = this.state.areaSelectedData
          .filter((a: any) => a.id !== "-1" && a.id !== "All")
          .map((a: any) => Number(a.id));
        if (areaIds.length > 0) {
          this.getStores(areaIds);
        }
      }
    })
  };
  
  setShowEndDate = (value: boolean) => {
    this.setState({ showEndDate: value });
  };

  // Product dropdown handlers
  handleProductScroll = () => {
    if (!this.isLoadingMoreProducts && this.hasMoreProducts) {
      const selectedSectionIds = this.state.sectionSelectedData
        .filter((s: any) => s.id !== "-1" && s.id !== "All")
        .map((s: any) => Number(s.id));
      
      this.getProducts(selectedSectionIds, this.productNextPage, this.productSearchQuery);
    }
  };

  handlePromotionItemProductScroll = (itemType: string) => {
    if (!this.isLoadingMorePromotionItemProducts && this.hasMorePromotionItemProducts) {
      const selectedSectionIds = this.state.sectionSelectedData
        .filter((s: any) => s.id !== "-1" && s.id !== "All")
        .map((s: any) => Number(s.id));
      
      this.getPromotionItemProducts(itemType, selectedSectionIds, this.promotionItemProductNextPage, this.promotionItemProductSearchQuery);
    }
  };

  handleOfferItemProductScroll = (itemType: string) => {
    if (!this.isLoadingMoreOfferItemProducts && this.hasMoreOfferItemProducts) {
      const selectedSectionIds = this.state.sectionSelectedData
        .filter((s: any) => s.id !== "-1" && s.id !== "All")
        .map((s: any) => Number(s.id));
      
      this.getOfferItemProducts(itemType, selectedSectionIds, this.offerItemProductNextPage, this.offerItemProductSearchQuery);
    }
  };

  handleProductSearch = (searchQuery: string) => {
    this.productSearchQuery = searchQuery;
    this.productNextPage = 1;
    
    const selectedSectionIds = this.state.sectionSelectedData
      .filter((s: any) => s.id !== "-1" && s.id !== "All")
      .map((s: any) => Number(s.id));
    
    if (searchQuery || selectedSectionIds.length > 0) {
      this.getProducts(selectedSectionIds, 1, searchQuery);
    }
  };

  handleSelectServiceOptions = (value: { id: string, option: string }[], option: { id: string, option: string }) => {
    let updateStateData: Partial<S> = {}
    if (option.id == "-1") {
      updateStateData = this.state.isServiceSelectAll ?
        { ...updateStateData, isServiceSelectAll: false, selectedServices: [], selectedServicesForList: [] }
        :
        {
          ...updateStateData,
          isServiceSelectAll: true,
          selectedServices: [{ id: "All", option: "Select All" }],
          selectedServicesForList: [{ id: "-1", option: "Select All" }, ...this.state.serviceList.map((item: any) => { return { id: item.id, option: item.name } })]
        };

    } else {
      const isValueContained = value.some((item) => item.id == option.id)
      value = value.filter((item) => item.id !== "-1")
      const newItems = isValueContained ? value.filter((item) => item.id != option.id) : [...value, option]
      const serviceUpdateData = this.updateMultiSelectData(
        newItems,
        this.state.serviceList.map((item: any) => { return { id: item.id, option: item.name } }),
        'isServiceSelectAll',
        'selectedServices',
        'selectedServicesForList',
      )
      updateStateData = { ...updateStateData, ...serviceUpdateData }
    }
    this.setState(updateStateData as Pick<S, keyof S>, () => {
      // Update the form field with the selected service IDs
      const selectedServiceIds = this.state.selectedServices?.filter((item: any) => item.id !== "All").map((item: any) => Number(item.id)) || [];
      const serviceIds = this.state.isServiceSelectAll ? ["All"] : selectedServiceIds;
      this.onChangeValue("service_ids", serviceIds);
    })
  };

  handleStoreMultiSelect = (value: { id: string, option: string }[], option: { id: string, option: string }) => {
    let updateStateData: Partial<S> = {}
    if (option.id == "-1") {
      updateStateData = this.state.isStoreSelectAll ?
        { ...updateStateData, isStoreSelectAll: false, selectedStoresForList: [] }
        :
        {
          ...updateStateData,
          isStoreSelectAll: true,
          selectedStoresForList: [{ id: "-1", option: "Select All" }, ...this.state.storeList.map((item: any) => { return { id: item.id, option: item.option || item.name } })]
        };

    } else {
      const isValueContained = value.some((item) => item.id == option.id)
      value = value.filter((item) => item.id !== "-1")
      const newItems = isValueContained ? value.filter((item) => item.id != option.id) : [...value, option]
      const storeUpdateData = this.updateMultiSelectData(
        newItems,
        this.state.storeList.map((item: any) => { return { id: item.id, option: item.option || item.name } }),
        'isStoreSelectAll',
        'selectedStoresForList',
        'selectedStoresForList',
      )
      updateStateData = { ...updateStateData, ...storeUpdateData }
    }
    this.setState(updateStateData as Pick<S, keyof S>, () => {
      // Update the form field with the selected store IDs
      let storeIdsForForm: any[];
      let storeIdsForAPI: number[];
      
      if (this.state.isStoreSelectAll) {
        // When "Select All" is chosen, use all store IDs for API call
        storeIdsForForm = ["All"];
        storeIdsForAPI = this.state.storeList.map((item: any) => Number(item.id));
      } else {
        // When specific stores are selected
        const selectedStoreIds = this.state.selectedStoresForList?.filter((item: any) => item.id !== "All" && item.id !== "-1").map((item: any) => Number(item.id)) || [];
        storeIdsForForm = selectedStoreIds;
        storeIdsForAPI = selectedStoreIds;
      }
      
      // Update legacy selectedStores for backward compatibility
      const selectedStores = this.state.isStoreSelectAll ? 
        this.state.storeList.map(item => ({
          id: item.id,
          name: item.name || item.option || '',
          label: item.name || item.option || '',
          option: item.name || item.option || '',
          store_name: item.store_name || item.name || item.option || ''
        })) :
        this.state.selectedStoresForList?.filter(item => item.id !== "-1" && item.id !== "All").map(item => ({
          id: item.id,
          name: item.option || '',
          label: item.option || '',
          option: item.option || '',
          store_name: item.option || ''
        })) || [];
      
      this.setState({ selectedStores });
      this.onChangeValue("store_ids", storeIdsForForm);
    })
  };

  handleExcludeDaysMultiSelect = (value: { id: string, option: string }[], option: { id: string, option: string }) => {
    // Handle excluded days selection
    const isValueContained = value.some((item) => item.id == option.id);
    const newItems = isValueContained 
      ? value.filter((item) => item.id != option.id) 
      : [...value, option];
    
    // Update the form field with excluded days in the expected format
    const excludedDays = newItems.map(item => ({
      day_of_week: item.option
    }));
    
    this.onChangeValue("excluded_days", excludedDays);
  };

  isAllProductsPromotion = () =>
    this.state.data.attributes.promotion_items.some(
      item => !item._destroy && item.item_type === "all_products"
    );

  getDynamicOfferTypes = () => {
    if (this.isAllProductsPromotion()) {
      return [
        { id: "all_products", name: "All Products (cheapest free)", label: "All Products (cheapest free)" }
      ];
    }

    // Check if any promotion item has "parent_product" selected
    const hasProductGroup = this.state.data.attributes.promotion_items.some(
      item => item.item_type === "parent_product"
    );
    
    if (hasProductGroup) {
      return [
        { id: "same_item", name: "Same Product Group", label: "Same Product Group" },
        { id: "different_item", name: "Different Specific Item/Group", label: "Different Specific Item/Group" }
      ];
    } else {
      return [
        { id: "same_item", name: "Same Item", label: "Same Item" },
        { id: "different_item", name: "Different Specific Item", label: "Different Specific Item" }
      ];
    }
  };

  // Customizable Area End
}

export { usageTypes, statusTypes, itemTypes, offerItemTypes, offerTypes, daysOfWeek };